import { useState, useContext } from "react";
import styles from "./styles.module.css";
import { Grid, MenuItem } from "@mui/material";
import CustomTextField from "../utils/CustomFields/CustomTextField";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ButtonRed from "../utils/CustomFields/ButtonRed";
import ButtonGray from "../utils/CustomFields/ButtonGray";
import { CREDIT_LIMIT_TYPES } from "const";
import CustomerContext from "context/CustomerContext";
import toast from "react-hot-toast";

function AddCustomer({ closeDialog, addCustomerHandler, searchText = "" }) {
  const { customers = [] } = useContext(CustomerContext) || {};
  const isNumeric = /^\d+$/.test(searchText.trim());

  const [data, setData] = useState({
    name: !isNumeric ? searchText : "",
    mobile: isNumeric ? searchText : "",
    credit_limit_type: CREDIT_LIMIT_TYPES.CREDIT,
    balance_type: CREDIT_LIMIT_TYPES.CREDIT,
  });
  const [mobileError, setMobileError] = useState("");

  const handleMobileChange = (mobileNumber) => {
    const cleanedMobileNumber = String(mobileNumber).trim();
    setData({ ...data, mobile: cleanedMobileNumber });
    if (
      cleanedMobileNumber &&
      customers.some((customer) => String(customer.mobile || "").trim() === cleanedMobileNumber)
    ) {
      const customer = customers.find((customer) => String(customer.mobile || "").trim() === cleanedMobileNumber);
      const errorText = `${customer?.name} have the same mobile number, use a different number`;
      setMobileError(errorText);
    } else {
      setMobileError("");
    }
  };

  const handleCreateCustomer = () => {
    if (!data.name || !data.mobile) {
      toast.error("Please fill in both Name and Mobile number.");
      return;
    }

    const cleanedMobile = String(data.mobile).trim();
    const existingCustomer = customers.find((c) => {
      const existingMob = String(c.mobile || "").trim();
      return existingMob && existingMob === cleanedMobile;
    });

    if (existingCustomer) {
      setMobileError("A customer with this mobile number already exists.");
      toast.error(
        `Customer with mobile number '${cleanedMobile}' already exists (${existingCustomer.name}). Please change the mobile number.`,
      );
      return;
    }

    const creditLimit = Number(data.credit_limit) || 0;
    const adjustedCreditLimit =
      data.credit_limit_type === CREDIT_LIMIT_TYPES.CREDIT ? -Math.abs(creditLimit) : Math.abs(creditLimit);

    const balance = Number(data.balance) || 0;
    const adjustedBalance = data.balance_type === CREDIT_LIMIT_TYPES.CREDIT ? -Math.abs(balance) : Math.abs(balance);

    const payload = {
      ...data,
      mobile: cleanedMobile,
      credit_limit: adjustedCreditLimit,
      balance: adjustedBalance,
    };

    addCustomerHandler(payload);
  };

  return (
    <div className={styles.popupOverlay}>
      <div className={styles.popupContainer} onClick={(e) => e.stopPropagation()}>
        <div className={styles.closeIcoSection}>
          <CloseRoundedIcon onClick={closeDialog} className={styles.icon} />
        </div>

        <div className={styles.headerSection} style={{ paddingBottom: "15px" }}>
          <div>
            <h2>Add Customer</h2>
            <h4>Register a new customer and store their information securely.</h4>
          </div>
        </div>

        <Grid container spacing={2} className={styles.contentArea} mb={2}>
          <Grid item xs={12}>
            <CustomTextField
              label="Name"
              placeholder="Enter name"
              value={data.name}
              onChange={(e) => setData({ ...data, name: e.target.value })}
            />
          </Grid>

          <Grid item xs={12}>
            <CustomTextField
              label="Mobile"
              placeholder="Enter mobile"
              value={data.mobile}
              onChange={(e) => handleMobileChange(e.target.value)}
              error={!!mobileError}
              helperText={mobileError}
            />
          </Grid>

          <Grid item xs={12}>
            <CustomTextField
              label="Location"
              placeholder="Enter location"
              value={data.location}
              onChange={(e) => setData({ ...data, location: e.target.value })}
            />
          </Grid>

          <Grid item xs={12}>
            <CustomTextField
              label="GSTIN"
              placeholder="Enter GSTIN"
              value={data.trn_number}
              onChange={(e) => setData({ ...data, trn_number: e.target.value })}
            />
          </Grid>

          <Grid item xs={5}>
            <CustomTextField
              select
              label="Credit Type"
              value={data.credit_limit_type}
              onChange={(e) => setData({ ...data, credit_limit_type: e.target.value })}
              sx={{
                "& .MuiSelect-select": {
                  color: data.credit_limit_type === CREDIT_LIMIT_TYPES.CREDIT ? "red" : "green",
                },
              }}
            >
              <MenuItem value={CREDIT_LIMIT_TYPES.CREDIT}>Credit</MenuItem>
              <MenuItem value={CREDIT_LIMIT_TYPES.ADVANCE}>Advance</MenuItem>
            </CustomTextField>
          </Grid>

          <Grid item xs={7}>
            <CustomTextField
              label="Credit Limit"
              placeholder="0.00"
              value={data.credit_limit}
              onChange={(e) => setData({ ...data, credit_limit: e.target.value })}
            />
          </Grid>

          <Grid item xs={5}>
            <CustomTextField
              select
              label="Balance Type"
              value={data.balance_type}
              onChange={(e) => setData({ ...data, balance_type: e.target.value })}
              sx={{
                "& .MuiSelect-select": {
                  color: data.balance_type === CREDIT_LIMIT_TYPES.CREDIT ? "red" : "green",
                },
              }}
            >
              <MenuItem value={CREDIT_LIMIT_TYPES.CREDIT}>Credit</MenuItem>
              <MenuItem value={CREDIT_LIMIT_TYPES.ADVANCE}>Advance</MenuItem>
            </CustomTextField>
          </Grid>

          <Grid item xs={7}>
            <CustomTextField
              label="Balance"
              placeholder="0.00"
              value={data.balance}
              onChange={(e) => setData({ ...data, balance: e.target.value })}
            />
          </Grid>
        </Grid>

        <div className={styles.actionButtons}>
          <ButtonGray onClick={closeDialog}>Cancel</ButtonGray>
          <ButtonRed onClick={handleCreateCustomer}>Create</ButtonRed>
        </div>
      </div>
    </div>
  );
}

export default AddCustomer;
